import { useMemo, useState, useEffect, useRef } from 'react';
import { z } from "zod";
import '../../styles/json-keys-config.css';
import dropdownArrowIcon from "../../assets/action-items/arrow-dropdown-icon.svg";
import IntegrationService from '../../services/integrations/Integrations';
import ProgressSpinner from '../Common/ProgressSpinner';
import useDisplaySnackbar from '../../utils/useDisplaySnackbar';

const formatEditorValue = (value: unknown) => JSON.stringify(value, null, 2) ?? '';
const ADD_NEW_KEY_OPTION = '__add_new_key__';
const ALL_KEYS_OPTION = 'All_keys';
const JsonValueSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema)
  ])
);
const JsonObjectSchema = z.record(z.string(), JsonValueSchema);
const buildSchema = (value: unknown): any => {
  if (Array.isArray(value)) {
    return {
      type: 'array',
      items: value.length > 0 ? buildSchema(value[0]) : {},
    };
  }
  if (value === null) {
    return { type: 'null' };
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return {
      type: 'object',
      properties: Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key, buildSchema(val)])
      ),
      additionalProperties: true,
    };
  }
  if (typeof value === 'boolean') {
    return { type: 'boolean' };
  }
  if (typeof value === 'number') {
    return Number.isInteger(value) ? { type: 'integer' } : { type: 'number' };
  }
  return { type: 'string' };
};

const AppConfigForm = ({ accountId }: { accountId: string }) => {
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [editorValue, setEditorValue] = useState<string>('');
  const [editorError, setEditorError] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [newKeyName, setNewKeyName] = useState<string>('');
  const [newKeyValue, setNewKeyValue] = useState<string>('""');
  const [newKeyError, setNewKeyError] = useState<string>('');
  const [lastSelectedKey, setLastSelectedKey] = useState<string>('');
  const [hasAppliedChanges, setHasAppliedChanges] = useState<boolean>(false);
  const [isValidated, setIsValidated] = useState<boolean>(false);
  const [isKeyDropdownOpen, setIsKeyDropdownOpen] = useState<boolean>(false);
  const keyDropdownRef = useRef<HTMLDivElement | null>(null);
  const editorPanelRef = useRef<HTMLDivElement | null>(null);
  const editorTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [previewOffsetTop, setPreviewOffsetTop] = useState<number>(0);
  const { showSnackbar } = useDisplaySnackbar();

  const configKeys = useMemo(() => Object.keys(formData), [formData]);
  const isAddMode = selectedKey === ADD_NEW_KEY_OPTION;
  const isAllMode = selectedKey === ALL_KEYS_OPTION;
  const selectedValueString = useMemo(
    () => {
      if (!selectedKey || isAddMode) {
        return '';
      }
      if (isAllMode) {
        return formatEditorValue(formData);
      }
      return formatEditorValue(formData[selectedKey]);
    },
    [formData, selectedKey, isAddMode, isAllMode]
  );
  const hasChanges = editorValue !== selectedValueString;
  const canAddKey = newKeyName.trim().length > 0 && newKeyValue.trim().length > 0;

  const { data: configResponse, isLoading, isFetching, isSuccess, isError, error, refetch } =
    IntegrationService.useGetAccountConfig(
      { accountId },
      !!accountId
    );

  useEffect(() => {
    setFormData({});
    setSelectedKey('');
    setEditorValue('');
    setEditorError('');
    setStatus('');
    setNewKeyName('');
    setNewKeyValue('""');
    setNewKeyError('');
    setLastSelectedKey('');
    setHasAppliedChanges(false);
    setIsValidated(false);
  }, [accountId]);
  
  useEffect(() => {
    if (!isSuccess || !configResponse) return;
    const rawConfig = configResponse?.data?.config ?? configResponse?.data;

    if (rawConfig) {
      let parsedConfig: Record<string, unknown> = {};
      if (typeof rawConfig === "string") {
        try {
          parsedConfig = JSON.parse(rawConfig);
        } catch {
          setEditorError('Failed to parse config JSON');
          return;
        }
      } else if (typeof rawConfig === "object") {
        parsedConfig = rawConfig as Record<string, unknown>;
      }

      // ✅ SET FORM DATA
      setFormData(parsedConfig);
      setOriginalData(parsedConfig);

      // ✅ Set default selected key
      if (Object.keys(parsedConfig).length > 0) {
        setSelectedKey(Object.keys(parsedConfig)[0]);
      }
    }
    if (isError) {
      showSnackbar("error", error || "Failed to load data.");
    }   
  }, [configResponse, isSuccess, isError, error]);

  const upsertMutation =
    IntegrationService.useUpsertAccountConfig({
      onSuccess: () => {
        setStatus("Config saved successfully");
        setIsValidated(false);
        setHasAppliedChanges(false);
        refetch();
      },
      onError: () => {
        setStatus("Failed to save config");
      },
    });

  useEffect(() => {
    if (!selectedKey && configKeys.length > 0) {
      setSelectedKey(configKeys[0]);
      return;
    }
    if (isAddMode) {
      setEditorError('');
      return;
    }
    if (selectedKey) {
      setEditorValue(selectedValueString);
      setEditorError('');
    }
  }, [selectedKey, selectedValueString, configKeys, isAddMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (keyDropdownRef.current && !keyDropdownRef.current.contains(event.target as Node)) {
        setIsKeyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const updatePreviewOffset = () => {
      const editorPanel = editorPanelRef.current;
      const editorTextarea = editorTextareaRef.current;
      if (!editorPanel || !editorTextarea) {
        setPreviewOffsetTop(0);
        return;
      }
      const panelRect = editorPanel.getBoundingClientRect();
      const textareaRect = editorTextarea.getBoundingClientRect();
      const offset = Math.max(0, Math.round(textareaRect.top - panelRect.top));
      setPreviewOffsetTop(offset);
    };

    updatePreviewOffset();
    window.addEventListener('resize', updatePreviewOffset);

    const editorPanel = editorPanelRef.current;
    const editorTextarea = editorTextareaRef.current;
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updatePreviewOffset)
      : null;

    if (observer && editorPanel) observer.observe(editorPanel);
    if (observer && editorTextarea) observer.observe(editorTextarea);

    return () => {
      window.removeEventListener('resize', updatePreviewOffset);
      observer?.disconnect();
    };
  }, [isAddMode, newKeyError, editorError, status, selectedKey, isKeyDropdownOpen]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const handleApplyChange = () => {
    if (!selectedKey) {
      return;
    }
    try {
      const parsedValue = JSON.parse(editorValue);
      if (isAllMode) {
        if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
          setEditorError('All config must be a JSON object');
          setStatus('');
          return;
        }
        setFormData(parsedValue as Record<string, unknown>);
        setHasAppliedChanges(true);
        setIsValidated(false);
        setEditorError('');
        setStatus('Applied all config');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        [selectedKey]: parsedValue,
      }));
      setHasAppliedChanges(true);
      setIsValidated(false);
      setEditorError('');
      setStatus(`Applied "${selectedKey}"`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON value';
      setEditorError(message);
      setStatus('');
    }
  };

  const handleDiscard = () => {
    if (isAddMode) return;
    if (isAllMode) {
      setFormData(originalData);
      setEditorValue(formatEditorValue(originalData));
    } else if (selectedKey) {
      setFormData(originalData);
      setEditorValue(formatEditorValue(originalData[selectedKey]));
    }

    setHasAppliedChanges(false);
    setIsValidated(false);
    setEditorError("");
    setStatus("Changes discarded");
  };

  const handleAddKey = () => {
    const keyName = newKeyName.trim();

    if (!keyName) {
      setNewKeyError('Key name is required');
      return;
    }
    if (Object.prototype.hasOwnProperty.call(formData, keyName)) {
      setNewKeyError(`"${keyName}" already exists`);
      return;
    }

    try {
      const parsedValue = JSON.parse(newKeyValue);
      setFormData((prev) => ({
        ...prev,
        [keyName]: parsedValue,
      }));
      setSelectedKey(keyName);
      setEditorValue(formatEditorValue(parsedValue));
      setHasAppliedChanges(true);
      setIsValidated(false);
      setNewKeyName('');
      setNewKeyValue('""');
      setNewKeyError('');
      setEditorError('');
      setStatus(`Applied new key "${keyName}"`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON value for new key';
      setNewKeyError(message);
    }
  };

  const handleDiscardAddKey = () => {
    const fallbackKey = configKeys.includes(lastSelectedKey) ? lastSelectedKey : configKeys[0] || '';
    setSelectedKey(fallbackKey);
    setNewKeyName('');
    setNewKeyValue('""');
    setNewKeyError('');
    setStatus('Add key cancelled');
  };

  const handleValidate = () => {
    try {
      const parsed = JSON.parse(editorValue);

      const schema = isAllMode ? JsonObjectSchema : JsonValueSchema;

      const result = schema.safeParse(parsed);

      if (!result.success) {
        setIsValidated(false);
        setEditorError(result.error.issues.map(i => i.message).join("; "));
        showSnackbar("error", "JSON validation failed");
        return;
      }

      setIsValidated(true);
      setEditorError("");
      showSnackbar("success", "JSON validation successful");

    } catch (err: any) {
      setIsValidated(false);
      setEditorError(err.message);
      showSnackbar("error", "Invalid JSON format");
    }
  };

  const handleSubmit = () => {
    if (!isValidated) return;

    upsertMutation.mutate(
      {
        accountId: String(accountId),
        config: formData,
      },
      {
        onSuccess: () => {
          showSnackbar("success", "Config saved successfully");
        },
        onError: () => {
          showSnackbar("error", "Failed to save config");
        }
      }
    );
  };

  const renderValue = (value: any, original: any): any => {
  // Primitive values
  if (typeof value !== "object" || value === null) {
    const isChanged = JSON.stringify(value) !== JSON.stringify(original);
    return (
      <span
        className={isChanged ? "json-keys-config__preview-line--changed" : ""}
      >
        {JSON.stringify(value)}
      </span>
    );
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return (
      <span>
        {"["}
        {value.map((item, i) => (
          <div key={i}>
            {renderValue(item, original?.[i])}
            {i < value.length - 1 && ","}
          </div>
        ))}
        {"]"}
      </span>
    );
  }

  // Handle objects
  return (
    <span>
      {"{"}
      {Object.entries(value).map(([k, v], i, arr) => (
        <div key={k}>
          <span>"{k}"</span>: {renderValue(v, original?.[k])}
          {i < arr.length - 1 && ","}
        </div>
      ))}
      {"}"}
    </span>
  );
};

  const renderAllPreview = () => {
    return Object.entries(formData).map(([key, value]) => {
      const isNewKey = !(key in originalData);

      return (
        <div
          key={key}
          className={`json-keys-config__preview-line ${
            isNewKey ? "json-keys-config__preview-line--changed" : ""
          }`}
        >
          <span>"{key}"</span>: {renderValue(value, originalData[key])}
        </div>
      );
    });
  };

  const isNewKey = !(selectedKey in originalData);
  const renderPreview = () => {
    if (isAddMode) {
      return <div>Select or create a key to see preview.</div>;
    }
    if (!selectedKey) {
      return <div>Select a key to see preview.</div>;
    }
    if (isAllMode) {
      return (
        <>
          {"{"}
          {renderAllPreview()}
          {"}"}
        </>
      );
    }
    return (
      <>
        {"{"}
        <div
          className={`json-keys-config__preview-line ${
            isNewKey ? "json-keys-config__preview-line--changed" : ""
          }`}
        >
          <span>"{selectedKey}"</span>: {renderValue(formData[selectedKey], originalData[selectedKey])}
        </div>
        {"}"}
      </>
    );
  };

  return (
    <div className="json-keys-config__body">
      <ProgressSpinner showSpinner={(isLoading && isFetching) || upsertMutation.isPending} />
      <div className="json-keys-config__editor-panel" ref={editorPanelRef}>
        <div className="json-keys-config__key-row">
          <label className="json-keys-config__label json-keys-config__label--inline">Key</label>
          <div className="json-keys-config__dropdown" ref={keyDropdownRef}>
            <button
              type="button"
              className="json-keys-config__dropdown-trigger"
              onClick={() => setIsKeyDropdownOpen((prev) => !prev)}
            >
              <span className="json-keys-config__dropdown-label">
                {isAddMode ? '+ Add new key' : selectedKey}
              </span>
              <img src={dropdownArrowIcon}/>
            </button>

            {isKeyDropdownOpen && (
              <div className="json-keys-config__dropdown-menu">
                <button
                  type="button"
                  className={`json-keys-config__dropdown-option ${isAllMode ? 'json-keys-config__dropdown-option--active' : ''}`}
                  onClick={() => {
                    setSelectedKey(ALL_KEYS_OPTION);
                    setEditorError('');
                    setStatus('');
                    setIsValidated(false);
                    setNewKeyError('');
                    setIsKeyDropdownOpen(false);
                  }}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`json-keys-config__dropdown-option ${isAddMode ? 'json-keys-config__dropdown-option--active' : ''}`}
                  onClick={() => {
                    setSelectedKey(ADD_NEW_KEY_OPTION);
                    setEditorError('');
                    setStatus('');
                    setIsValidated(false);
                    setNewKeyError('');
                    setIsKeyDropdownOpen(false);
                  }}
                >
                  + Add new key
                </button>
                {configKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={`json-keys-config__dropdown-option ${selectedKey === key ? 'json-keys-config__dropdown-option--active' : ''}`}
                    onClick={() => {
                      setLastSelectedKey(key);
                      setSelectedKey(key);
                      setEditorError('');
                      setStatus('');
                      setIsValidated(false);
                      setNewKeyError('');
                      setIsKeyDropdownOpen(false);
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {isAddMode && (
          <>
            <label className="json-keys-config__label json-keys-config__label--spaced">New Key Name</label>
            <input
              type="text"
              className="json-keys-config__input"
              value={newKeyName}
              placeholder="Enter key name"
              onChange={(event) => {
                setNewKeyName(event.target.value);
                if (newKeyError) {
                  setNewKeyError('');
                }
                setIsValidated(false);
              }}
            />
          </>
        )}

        <label className="json-keys-config__label json-keys-config__label--spaced">
          {isAddMode ? 'New Key Value' : 'Current Value'}
        </label>
        <textarea
          ref={editorTextareaRef}
          value={isAddMode ? newKeyValue : editorValue}
          onChange={(event) => {
            if (isAddMode) {
              setNewKeyValue(event.target.value);
              if (newKeyError) {
                setNewKeyError('');
              }
              setIsValidated(false);
            } else {
              setEditorValue(event.target.value);
              if (editorError) {
                setEditorError('');
              }
              if (status) {
                setStatus('');
              }
              setIsValidated(false);
            }
          }}
          className="json-keys-config__textarea"
        />

        {newKeyError && <p className="json-keys-config__error">{newKeyError}</p>}
        {editorError && <p className="json-keys-config__error">{editorError}</p>}
        {!editorError && status && <p className="json-keys-config__success">{status}</p>}
      </div>
      <div className="json-keys-config__actions json-keys-config__actions--middle">
        <div className="json-keys-config__flow-arrow" aria-hidden="true">
          &rarr;
        </div>
        <button
          type="button"
          onClick={isAddMode ? handleAddKey : handleApplyChange}
          className="json-keys-config__save-btn"
          disabled={isAddMode ? !canAddKey : !hasChanges}
        >
          {isAddMode ? 'Apply New Key' : isAllMode ? 'Apply Config' : 'Apply Change'}
        </button>
        {!isAddMode && (
          <button
            type="button"
            onClick={handleDiscard}
            className="json-keys-config__discard-btn"
            disabled={!hasUnsavedChanges}
          >
            Discard
          </button>
        )}
        {isAddMode && (
          <button
            type="button"
            onClick={handleDiscardAddKey}
            className="json-keys-config__discard-btn"
            disabled={!newKeyName.trim() && !newKeyValue.trim()}
          >
            Discard
          </button>
        )}
      </div>

      <div
        className={`json-keys-config__preview-column ${
          isAddMode ? "json-keys-config__preview-column--add-mode" : ""
        }`}
        style={{ ['--preview-offset-top' as any]: `${previewOffsetTop}px`, marginTop: "-24px" }}
      >
        <label className="json-keys-config__label">
          Proposed Value
        </label>
        <div className="json-keys-config__preview-panel">
          <div className="json-keys-config__preview-json">
            {renderPreview()}
          </div>
        </div>
        <div className="json-keys-config__actions json-keys-config__actions--preview">
          <button
            type="button"
            onClick={handleValidate}
            className="json-keys-config__validate-btn"
            disabled={isAddMode || hasChanges || !hasAppliedChanges || isValidated}
          >
            Validate JSON
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="json-keys-config__save-btn"
            disabled={!isValidated}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppConfigForm;
